import React, { useEffect, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import qs from 'qs';
import MuiGrid from '@material-ui/core/Grid';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';

import { Params } from 'types';
import { GridBodyType } from 'enums';

import { GridParams, GridProps } from './grid-types';
import {
  useFetchDetail,
  useFetchList,
  useGridBreadcrumbs,
  useTransitionButtons,
} from './grid-hooks';
import { GridLeftPanel as DefaultGridLeftPanel } from './left-panel/grid-left-panel';
import { GridUpperPanel as DefaultGridUpperPanel } from './upper-panel/grid-upper-panel';
import { GridCentralPanel as DefaultGridCentralPanel } from './central-panel/grid-central-panel';
import { GridRightPanel as DefaultGridRightPanel } from './right-panel/grid-right-panel';
import { useRootArchivalAid } from './grid-hooks/root-archival-aid-hook';
import { getItemFromNestedEadTree } from 'utils/get-item-from-nested-ead-tree';
import { ItemHierarchyContextProvider } from './grid-hooks/item-hierarchy-context-hook';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    gridRoot: {
      minHeight: 'calc(100vh - 80px)',
    },
    gridCenterRoot: {
      gridGap: theme.spacing(3),
    },
    upperPanel: {
      position: 'sticky',
      // because of the header height
      top: 80,
      zIndex: 5,
    },
  })
);

export function Grid(props: GridProps) {
  const {
    GridLeftPanel = DefaultGridLeftPanel,
    GridUpperPanel = DefaultGridUpperPanel,
    GridCentralPanel = DefaultGridCentralPanel,
    GridRightPanel = DefaultGridRightPanel,
    leftPanelProps = {},
    centralPanelProps = {},
    upperPanelProps = {},
    rightPanelProps = {},
    gridConfig = [],
  } = props;

  if (gridConfig.length === 0) {
    throw new Error('empty grid config');
  }

  const [gridEntityConfig] = gridConfig;
  const {
    id: idRoute,
    urlRoute,
    listApi,
    params: defaultParams = {},
    archivalAidLevel,
  } = gridEntityConfig;

  const classes = useStyles();
  const { id, level = '0', ead: eadParam } = useParams<GridParams>();
  const { location, replace } = useHistory();
  const [gridBody, setGridBody] = useState<GridBodyType>(GridBodyType.DETAIL);
  const [params, setParams] = useState<Params>(defaultParams);
  const removeParams = () => setParams(defaultParams);

  const locationSearch = location.search;
  const levelIndex = Number(level) || 0;
  const detailRoute = gridConfig[levelIndex]?.apiRoute;
  const defaultDetailApi = gridConfig[levelIndex]?.detailApi;

  const detailApi = defaultDetailApi ?? `/api/${detailRoute!}/${id!}`;

  const isEad = eadParam !== undefined;

  const { items, count, fetchMore, refreshList } = useFetchList(
    listApi!,
    params,
    !!locationSearch
  );
  const { item, refreshDetail } = useFetchDetail(detailApi, id, isEad);

  const { rootArchivalAid, refreshRootArchivalAid } = useRootArchivalAid(
    isEad || Number(level) === archivalAidLevel,
    Number(level),
    id,
    archivalAidLevel
  );

  const eadItem = getItemFromNestedEadTree(id, rootArchivalAid?.eadNode);

  const hierarchyPath = useGridBreadcrumbs({
    id,
    item: isEad ? eadItem : item,
    level: levelIndex,
    gridConfig,
    isEad,
    rootArchivalAid,
    archivalAidLevel,
  });

  const { handleNext, handlePrevious } = useTransitionButtons({
    itemId: item?.id,
    urlRoute: urlRoute!,
    items,
    level: levelIndex,
  });

  const itemsLength = items?.length;
  const firstItemId = items?.[0]?.id;

  const hideHierarchy = leftPanelProps.hide === true;

  useEffect(() => {
    if (!!locationSearch) {
      const params: Params = qs.parse(locationSearch, {
        ignoreQueryPrefix: true,
      });
      setParams(params);
      replace(`/${idRoute}`);
    } else if (!id && !!itemsLength) {
      replace(`/${idRoute}/0/${firstItemId}`);
    }
  }, [firstItemId, id, idRoute, itemsLength, locationSearch, replace]);

  useEffect(() => {
    setGridBody(GridBodyType.DETAIL);
  }, [id]);

  return (
    <MuiGrid container className={classes.gridRoot}>
      {!hideHierarchy && (
        <MuiGrid item md={3} xs={12}>
          <GridLeftPanel
            rootArchivalAid={rootArchivalAid}
            archivalAidLevel={archivalAidLevel}
            items={items}
            count={count}
            fetchMore={fetchMore}
            gridConfig={gridConfig}
            hierarchyPath={hierarchyPath}
            params={params}
            removeParams={removeParams}
            {...leftPanelProps}
          />
        </MuiGrid>
      )}

      <MuiGrid item md={hideHierarchy ? 12 : 9} xs={12}>
        <MuiGrid container>
          <MuiGrid item xs={12} className={classes.upperPanel}>
            <GridUpperPanel
              hierarchyPath={hierarchyPath}
              {...upperPanelProps}
            />
          </MuiGrid>
          <ItemHierarchyContextProvider value={{ hierarchyPath }}>
            <MuiGrid item md={9} xs={12}>
              <GridCentralPanel
                rootArchivalAid={rootArchivalAid}
                item={isEad ? eadItem : item}
                gridBody={gridBody}
                setGridBody={setGridBody}
                handleNext={handleNext}
                handlePrevious={handlePrevious}
                gridConfig={gridConfig}
                hideTransitionButtons={
                  centralPanelProps.hideTransitionButtons || Number(level) > 0
                }
                {...centralPanelProps}
              />
            </MuiGrid>
          </ItemHierarchyContextProvider>
          <MuiGrid item md={3} xs={12}>
            <GridRightPanel
              rootArchivalAid={rootArchivalAid}
              item={isEad ? eadItem : item}
              isEad={isEad}
              gridConfig={gridConfig}
              gridBody={gridBody}
              setGridBody={setGridBody}
              refreshDetail={refreshDetail}
              refreshList={refreshList}
              refreshRootArchivalAid={refreshRootArchivalAid}
              {...rightPanelProps}
            />
          </MuiGrid>
        </MuiGrid>
      </MuiGrid>
    </MuiGrid>
  );
}
